/**
 * OAuthConnectButton
 *
 * Handles the full OAuth 2.0 + PKCE flow for APIs that declare oauth2 / openIdConnect
 * security schemes. This is shown INSTEAD of the credentials box when OAuth is available.
 *
 * ── What is PKCE? ────────────────────────────────────────────────────────────
 * PKCE (Proof Key for Code Exchange, pronounced "pixie") is the OAuth 2.1 standard
 * for public clients (like SPAs). Instead of a client secret, we generate:
 *
 *   1. code_verifier  — a random 64-char URL-safe string (lives only in this component)
 *   2. code_challenge — BASE64URL(SHA256(ASCII(code_verifier))) — sent to the provider
 *
 * The provider stores the challenge. When we exchange the authorization code for a token,
 * we send the original verifier. The provider hashes it and checks it matches the challenge.
 * This prevents anyone who intercepts the authorization code from using it.
 *
 * ── Flow ─────────────────────────────────────────────────────────────────────
 *  1. User clicks "Connect with [Provider]"
 *  2. We generate code_verifier + code_challenge (PKCE)
 *  3. POST /api/v1/oauth/start → gets authorization_url + state
 *  4. Open authorization_url in a popup window (or same tab — see note below)
 *  5. User approves → provider redirects to /oauth/callback?code=...&state=...
 *  6. Callback page POSTs to /api/v1/oauth/exchange
 *  7. Backend exchanges code for token, returns encrypted_token
 *  8. OAuthConnectButton receives the encrypted_token and calls onSuccess(token)
 *  9. Parent passes token as credentials.oauth_access_token_enc to /api/v1/host
 *
 * ── Popup vs same-tab ────────────────────────────────────────────────────────
 * We try a popup first (better UX — user stays on the conversion page).
 * If the browser blocks popups, we fall back to same-tab redirect. The callback
 * page detects whether it's in a popup and either postMessages back to the opener
 * or navigates back using sessionStorage.
 *
 * ── Per-endpoint OAuth (scopes filtering) ─────────────────────────────────
 * Some APIs define different OAuth scopes per endpoint. The `scopesByEndpoint`
 * prop (optional) lets the parent pass a map of {operationId → [scopes]}.
 * We union all the required scopes and request them together. For simpler APIs,
 * just pass `scopes` as a flat string.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ExternalLink, RefreshCw, ShieldCheck, Unplug } from "lucide-react";
import { getConverterApiBase } from "../api";
import type { AuthScheme } from "./AuthConfigStep";

// ── PKCE helpers ──────────────────────────────────────────────────────────────

/** Generate a cryptographically random code_verifier (43-128 chars, URL-safe base64). */
async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(48); // 48 bytes → 64 base64url chars
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** Compute code_challenge = BASE64URL(SHA256(ASCII(verifier))). */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

// ── OAuth config extracted from an OpenAPI auth scheme ────────────────────────

export type OAuthProviderConfig = {
  /** The provider's authorization endpoint URL */
  authorizeUrl: string;
  /** The provider's token endpoint URL */
  tokenUrl: string;
  /** OAuth client ID (from the spec or user-entered) */
  clientId: string;
  /** Space-separated list of scopes to request */
  scopes: string;
  /** Optional client secret (for providers that require it even with PKCE) */
  clientSecret?: string;
  /** Extra params for the authorization URL (e.g. access_type=offline) */
  extraParams?: Record<string, string>;
  /** Human-readable provider name */
  providerName?: string;
};

// ── Server-managed provider (one-click OAuth) ─────────────────────────────────
// Shape returned by GET /api/v1/oauth/providers. These are providers the app
// owner has pre-registered an OAuth app for, so the user needs no Client ID.

export type ServerProvider = {
  key: string;
  display_name: string;
  match_hosts: string[];
  authorize_url: string;
  token_url: string;
  default_scopes: string;
};

/** Lower-cased host of a URL, or '' if it can't be parsed. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

/** True if `host` equals `candidate` or is a subdomain of it. */
function hostMatches(host: string, candidate: string): boolean {
  if (!host || !candidate) return false;
  const c = candidate.toLowerCase();
  return host === c || host.endsWith("." + c);
}

/**
 * Given the list of server-managed providers and this spec's OAuth config,
 * return the matching provider (so we can do one-click), or null.
 */
export function matchServerProvider(
  providers: ServerProvider[],
  config: OAuthProviderConfig,
): ServerProvider | null {
  const hosts = [hostOf(config.authorizeUrl), hostOf(config.tokenUrl)].filter(Boolean);
  for (const provider of providers) {
    for (const host of hosts) {
      for (const candidate of provider.match_hosts) {
        if (hostMatches(host, candidate)) return provider;
      }
    }
  }
  return null;
}

/** Extract OAuth config from an OpenAPI auth scheme array, if possible. */
export function extractOAuthConfig(schemes: AuthScheme[]): OAuthProviderConfig | null {
  const oauthScheme = schemes.find(s => s.type === "oauth2" || s.type === "openIdConnect");
  if (!oauthScheme) return null;

  // The OpenAPI spec may embed URLs in the scheme object via is_complex / extended fields.
  // We use the raw scheme object cast as any to read oauth2-specific fields.
  const raw = oauthScheme as any;

  // Try to extract URLs from common OpenAPI oauth2 flow structures:
  // spec.security_schemes[n].flows.authorizationCode.authorizationUrl / tokenUrl
  const flows = raw.flows || {};
  const flow =
    flows.authorizationCode ||
    flows.implicit ||
    flows.clientCredentials ||
    flows.password ||
    null;

  const authorizeUrl = flow?.authorizationUrl || raw.authorizationUrl || raw.authorization_url || "";
  const tokenUrl = flow?.tokenUrl || raw.tokenUrl || raw.token_url || "";
  const scopes = Array.isArray(raw.scopes)
    ? raw.scopes.join(" ")
    : typeof raw.scopes === "string"
      ? raw.scopes
      : Object.keys(flow?.scopes || {}).join(" ");

  // Client ID must be provided by the user (it's never in the spec for security reasons)
  const clientId = raw.clientId || raw.client_id || "";

  if (!authorizeUrl || !tokenUrl) return null;

  return {
    authorizeUrl,
    tokenUrl,
    clientId,
    scopes,
    providerName: raw.providerName || undefined,
  };
}

// ── Component props ───────────────────────────────────────────────────────────

type OAuthState =
  | "idle"
  | "opening"       // waiting for popup to open / redirect
  | "waiting"       // popup opened, waiting for callback
  | "exchanging"    // exchanging code for token
  | "success"       // token received and encrypted
  | "error";

type Props = {
  /** The OAuth provider config (URLs, client_id, scopes). */
  config: OAuthProviderConfig;
  /** Called with the encrypted token blob when auth completes. */
  onSuccess: (encryptedToken: string) => void;
  /** Called when the user explicitly disconnects. */
  onDisconnect?: () => void;
  /** Whether the token is already connected (controlled). */
  isConnected?: boolean;
  /** If true, show a compact inline version without the outer card. */
  compact?: boolean;
  /** Match AuthDetectionBanner primary button (embedded under OAuth notice). */
  appearance?: "default" | "banner";
};

// ── Callback URL that the OAuth provider must be configured to redirect to ────
// This must be registered in the OAuth app's allowed redirect URIs.
function getOAuthCallbackUrl(): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/oauth/callback`;
}

export default function OAuthConnectButton({
  config,
  onSuccess,
  onDisconnect,
  isConnected = false,
  compact: _compact = false,
  appearance = "default",
}: Props) {
  const [oauthState, setOauthState] = useState<OAuthState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [clientId, setClientId] = useState(config.clientId || "");
  const [clientSecret, setClientSecret] = useState(config.clientSecret || "");
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clientIdInputRef = useRef<HTMLInputElement | null>(null);

  // ── Server-managed (one-click) detection ─────────────────────────────────
  // We ask the backend which providers it has pre-registered OAuth apps for.
  // If this spec's authorize/token URL matches one, the user does NOT need to
  // enter a Client ID — the backend supplies it. `null` while still loading.
  const [serverProvider, setServerProvider] = useState<ServerProvider | null>(null);
  const [providersLoaded, setProvidersLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${getConverterApiBase()}/api/v1/oauth/providers`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const providers: ServerProvider[] = Array.isArray(data?.providers)
          ? data.providers
          : [];
        const match = matchServerProvider(providers, config);
        if (!cancelled) {
          setServerProvider(match);
          setProvidersLoaded(true);
        }
      } catch {
        // If the providers endpoint fails, fall back to the BYO Client ID flow.
        if (!cancelled) {
          setServerProvider(null);
          setProvidersLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [config]);

  // The user must enter a Client ID only when the spec doesn't carry one AND
  // this isn't a server-managed provider.
  const needsClientId = !config.clientId && !serverProvider;

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Listen for postMessage from popup callback page
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Security: only accept messages from our own origin
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "OAUTH_CALLBACK") return;

      const { code, state, error: cbError } = event.data;

      if (cbError) {
        setOauthState("error");
        setError(cbError === "access_denied"
          ? "You denied access. Click Connect to try again."
          : `OAuth error: ${cbError}`);
        return;
      }

      if (code && state) {
        handleOAuthCallback(code, state);
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Also check sessionStorage in case popup was blocked and we used redirect
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = sessionStorage.getItem("oauth_callback_data");
    if (stored) {
      sessionStorage.removeItem("oauth_callback_data");
      try {
        const { code, state } = JSON.parse(stored);
        if (code && state) {
          handleOAuthCallback(code, state);
        }
      } catch {}
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOAuthCallback = useCallback(async (code: string, state: string) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    try {
      popupRef.current?.close();
    } catch {}

    setOauthState("exchanging");

    try {
      const resp = await fetch(`${getConverterApiBase()}/api/v1/oauth/exchange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, state }),
      });

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        const msg = errBody?.detail || errBody?.error || `Token exchange failed (HTTP ${resp.status})`;
        throw new Error(msg);
      }

      const data = await resp.json();
      const encryptedToken = data.encrypted_token as string;

      if (!encryptedToken) {
        throw new Error("No token received from OAuth exchange");
      }

      setOauthState("success");
      onSuccess(encryptedToken);
    } catch (err: any) {
      setOauthState("error");
      setError(err?.message || "OAuth token exchange failed. Please try again.");
    }
  }, [onSuccess]);

  const handleConnect = useCallback(async () => {
    setError(null);

    if (needsClientId && !clientId.trim()) {
      setError(
        "Enter your OAuth Client ID first — create an OAuth app in the API provider's developer portal, then paste its Client ID in the field above.",
      );
      // Pull the user's attention to the field they need to fill
      const input = clientIdInputRef.current;
      if (input) {
        input.scrollIntoView({ behavior: "smooth", block: "center" });
        input.focus();
      }
      return;
    }

    setOauthState("opening");

    try {
      // Generate PKCE pair
      const verifier = await generateCodeVerifier();
      const challenge = await generateCodeChallenge(verifier);

      const redirectUri = getOAuthCallbackUrl();

      // For server-managed providers, send the provider's canonical URLs and
      // an empty client_id/secret — the backend recognises the provider and
      // injects its own credentials. For BYO providers, send what we have.
      const startBody = serverProvider
        ? {
            authorize_url: serverProvider.authorize_url,
            token_url: serverProvider.token_url,
            client_id: "",        // backend supplies it
            scopes: config.scopes || serverProvider.default_scopes || "",
            redirect_uri: redirectUri,
            code_challenge: challenge,
            code_verifier: verifier,
            extra_params: config.extraParams || {},
            client_secret: "",    // backend supplies it
          }
        : {
            authorize_url: config.authorizeUrl,
            token_url: config.tokenUrl,
            client_id: clientId.trim() || config.clientId,
            scopes: config.scopes || "",
            redirect_uri: redirectUri,
            code_challenge: challenge,
            code_verifier: verifier,
            extra_params: config.extraParams || {},
            client_secret: clientSecret.trim() || config.clientSecret || "",
          };

      // Start the OAuth flow on the backend
      const startResp = await fetch(`${getConverterApiBase()}/api/v1/oauth/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(startBody),
      });

      if (!startResp.ok) {
        const errBody = await startResp.json().catch(() => ({}));
        throw new Error(errBody?.detail || "Failed to start OAuth flow");
      }

      const { authorization_url: authUrl } = await startResp.json();

      // Try to open a popup (800×700 centered on screen)
      const width = 800;
      const height = 700;
      const left = Math.round(window.screen.width / 2 - width / 2);
      const top = Math.round(window.screen.height / 2 - height / 2);

      const popup = window.open(
        authUrl,
        "oauth_popup",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
      );

      if (popup && !popup.closed) {
        popupRef.current = popup;
        setOauthState("waiting");

        // Poll for popup close (user closed without completing)
        pollIntervalRef.current = setInterval(() => {
          if (popup.closed) {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            // If we're still waiting (no postMessage received), the user closed the popup
            setOauthState(prev => {
              if (prev === "waiting") {
                setError("You closed the OAuth window. Click Connect to try again.");
                return "idle";
              }
              return prev;
            });
          }
        }, 500);
      } else {
        // Popup blocked — fall back to redirect in the same tab
        // Store the current URL so callback page can redirect back
        sessionStorage.setItem("oauth_return_to", window.location.href);
        window.location.href = authUrl;
      }
    } catch (err: any) {
      setOauthState("error");
      setError(err?.message || "Failed to start OAuth flow. Please try again.");
    }
  }, [config, clientId, clientSecret, needsClientId, serverProvider]);

  const handleDisconnect = useCallback(() => {
    setOauthState("idle");
    setError(null);
    onDisconnect?.();
  }, [onDisconnect]);

  const isLoading = oauthState === "opening" || oauthState === "waiting" || oauthState === "exchanging";
  // Prefer the server-managed provider's display name (e.g. "GitHub") so the
  // button reads "Connect with GitHub" once we know it's a one-click provider.
  const providerName =
    serverProvider?.display_name || config.providerName || "OAuth Provider";

  // ── Success state ──────────────────────────────────────────────────────────
  if (oauthState === "success" || isConnected) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between gap-3 py-2"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-green-100 dark:bg-green-900/30">
            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground leading-tight">
              Connected via OAuth
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your access is authorised · credentials stored encrypted
            </p>
          </div>
        </div>
        {onDisconnect && (
          <button
            type="button"
            onClick={handleDisconnect}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <Unplug className="w-3.5 h-3.5" />
            Disconnect
          </button>
        )}
      </motion.div>
    );
  }

  const isBanner = appearance === "banner";

  // ── Main connect UI ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {!isBanner ? (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-muted flex-shrink-0">
            <ShieldCheck className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground leading-tight">
              {serverProvider ? `Connect with ${serverProvider.display_name}` : "Connect with OAuth"}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {serverProvider
                ? "One-click — no API keys or setup needed"
                : "Authorise access securely — no API keys needed"}
            </p>
          </div>
          <span className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
            <ShieldCheck className="w-3.5 h-3.5" />
            PKCE secured
          </span>
        </div>
      ) : null}

      {/* Client ID input — shown only once we know this isn't a one-click
          (server-managed) provider, so the field never flashes for GitHub etc. */}
      {providersLoaded && needsClientId && (
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
            OAuth Client ID
            <span className="text-muted-foreground/60 font-normal ml-1">
              (from your OAuth app registration)
            </span>
          </label>
          <input
            ref={clientIdInputRef}
            type="text"
            autoComplete="off"
            placeholder="your-client-id"
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 transition font-mono"
          />
          <p className="text-[11px] text-muted-foreground mt-1.5">
            Create an OAuth app in the API provider's developer portal and paste the Client ID here.
          </p>
        </div>
      )}

      {/* Optional client secret — some providers require it even with PKCE */}
      {providersLoaded && needsClientId && (
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
            Client Secret
            <span className="text-muted-foreground/60 font-normal ml-1">
              (optional — only if the provider requires it)
            </span>
          </label>
          <input
            type="password"
            autoComplete="new-password"
            placeholder="Leave empty if not required"
            value={clientSecret}
            onChange={e => setClientSecret(e.target.value)}
            className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 transition font-mono"
          />
        </div>
      )}

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            key="err"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 px-4 py-3 text-xs text-red-700 dark:text-red-300"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status message while waiting */}
      <AnimatePresence>
        {oauthState === "waiting" && (
          <motion.p
            key="waiting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-xs text-muted-foreground text-center"
          >
            Waiting for authorisation in the popup window…
          </motion.p>
        )}
        {oauthState === "exchanging" && (
          <motion.p
            key="exchanging"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-xs text-muted-foreground text-center"
          >
            Exchanging authorisation code for access token…
          </motion.p>
        )}
      </AnimatePresence>

      {/* Connect button — clickable once the providers list has loaded (so we
          know one-click vs BYO) and not mid-flow. If a Client ID is required
          but missing, handleConnect surfaces a clear error and focuses the
          field, instead of the button silently doing nothing. */}
      <motion.button
        type="button"
        onClick={handleConnect}
        disabled={isLoading || !providersLoaded}
        whileHover={!isLoading ? { scale: 1.02 } : undefined}
        whileTap={!isLoading ? { scale: 0.98 } : undefined}
        className={
          isBanner
            ? "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            : "w-full inline-flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl text-sm font-semibold bg-foreground text-background hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-soft"
        }
      >
        {isLoading || !providersLoaded ? (
          <RefreshCw className={`animate-spin ${isBanner ? "w-3.5 h-3.5" : "w-4 h-4"}`} />
        ) : isBanner ? (
          <ShieldCheck className="w-3.5 h-3.5" />
        ) : (
          <ExternalLink className="w-4 h-4" />
        )}
        {!providersLoaded
          ? "Loading…"
          : oauthState === "opening"
          ? "Opening authorisation…"
          : oauthState === "waiting"
          ? "Waiting for approval…"
          : oauthState === "exchanging"
          ? "Getting access token…"
          : isBanner
            ? `Connect with ${serverProvider ? serverProvider.display_name : "OAuth"}`
            : `Connect with ${providerName}`}
      </motion.button>

      <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
        Uses OAuth 2.0 + PKCE — the most secure authorisation standard.
        Your credentials are never stored on our servers in plaintext.
      </p>
    </div>
  );
}
